//save to local
import FavoritesManager, { updateFavGroups } from '../FavoritesManager';
import { apiGet, apiPost } from '../../util/HttpApi';
import TracksManager, {
    createTrackGroups,
    isTrackExists,
    isEmptyTrack,
    getGpxFiles,
    filterSmartFolders,
    DEFAULT_GROUP_NAME,
    GPX_FILE_TYPE,
    getGpxFileFromTrackData,
    EMPTY_FILE_NAME,
    TRACK_VISIBLE_FLAG,
    preparedGpxFile,
    GPX_FILE_EXT,
    INFO_FILE_EXT,
    KMZ_FILE_EXT,
    prepareName,
    renameLastFolderSegment,
    updateMetadata,
    calculateLastModified,
} from './TracksManager';
import { syncCloudTrackInfo, findInfoFile } from './TrackAppearanceManager';
import isEmpty from 'lodash-es/isEmpty';
import { OBJECT_TYPE_CLOUD_TRACK, OBJECT_TYPE_FAVORITE, OBJECT_TYPE_LOCAL_TRACK } from '../../context/AppContext';
import { getFilesForUpdateDetails } from '../../util/hooks/useInitialFilesLoad';
import { cloneTrackObject, FILE_NAME_MAX_BYTES, sanitizedFileName, truncateToBytes } from '../../util/Utils';
import i18n from '../../i18n';
import { updateSortList } from '../../menu/actions/SortActions';
import { deleteLocalTrack, saveTrackToLocalStorage } from '../../context/LocalTrackStorage';
export function saveTrackToLocal({ ctx, track, selected = true, overwrite = false, cloudAutoSave = false } = {}) {
    const newLocalTracks = [...ctx.localTracks];

    if (!track?.name) {
        ctx.setRoutingErrorMsg('⚠️ Cannot save nameless local track.');
        return;
    }

    const originalName = removeFileExtension(track.name) + GPX_FILE_EXT;
    let localName = TracksManager.prepareName(originalName, true);

    // find free name
    if (overwrite === false) {
        localName = createTrackFreeName(localName, newLocalTracks);
    }

    TracksManager.prepareTrack(track, localName, originalName);

    closeCloudTrack(ctx, track);

    if (selected === true) {
        // upload 1 gpx - edit instantly
        openNewLocalTrack({ ctx, track, cloudAutoSave });
    } else {
        // used when multi-gpx uploaded
        // generate points and save track
        track.points = TracksManager.getEditablePoints(track);
        saveTrackToLocalStorage({ ctx, track });
    }

    if (overwrite) {
        const foundIndex = newLocalTracks?.findIndex((t) => t?.name === localName);
        if (foundIndex !== -1) {
            newLocalTracks[foundIndex] = track;
        } else {
            newLocalTracks.push(track);
        }
    } else {
        newLocalTracks.push(track);
    }

    ctx.setLocalTracks(newLocalTracks); // finally
}

//save to cloud
export async function saveTrackToCloud({
    ctx,
    ltx,
    currentFolder,
    fileName,
    type,
    gpxFile = null,
    uploadedFile = null,
    open = true,
}) {
    const trackData = gpxFile ?? ctx.selectedGpxFile?.file ?? ctx.selectedGpxFile;
    const routeTypes = ctx.selectedGpxFile.routeTypes;
    const currentFile = await getFile(trackData);
    async function getFile(trackData) {
        if (uploadedFile) {
            return uploadedFile;
        }
        if (trackData) {
            const gpx = await getGpxFileFromTrackData(trackData, routeTypes);
            const data = gpx?.data;
            if (data) {
                return {
                    originalName: trackData.originalName ?? trackData.name + GPX_FILE_EXT,
                    data,
                };
            }
        }
        return null;
    }

    if (type !== FavoritesManager.FAVORITE_FILE_TYPE) {
        if (currentFolder === DEFAULT_GROUP_NAME) {
            currentFolder = '';
        } else {
            currentFolder = currentFolder + '/';
        }
    }

    if (ltx.loginUser) {
        let serverError = null;
        if (currentFile) {
            let convertedData;
            if (currentFile.originalName.toLowerCase().endsWith(KMZ_FILE_EXT)) {
                convertedData = new Uint8Array(currentFile.data);
            } else {
                convertedData = new TextEncoder().encode(currentFile.data);
            }
            const zippedResult = require('pako').gzip(convertedData, { to: 'Uint8Array' });
            const convertedZipped = zippedResult.buffer;
            const oMyBlob = new Blob([convertedZipped], { type: 'gpx' });
            const data = new FormData();
            data.append('file', oMyBlob, currentFile.originalName);

            const params = {
                type: type,
                name:
                    type === FavoritesManager.FAVORITE_FILE_TYPE
                        ? currentFolder
                        : currentFolder + fileName + GPX_FILE_EXT,
            };

            // close possibly loaded Cloud track (clean up layers)
            ctx.mutateGpxFiles((o) => o[params.name] && (o[params.name].url = null));
            const res = await apiPost(`${process.env.REACT_APP_USER_API_SITE}/mapapi/upload-file`, data, {
                params,
                dataOnErrors: true,
            });
            if (res?.data?.status === 'ok') {
                if (type !== FavoritesManager.FAVORITE_FILE_TYPE) {
                    await syncCloudTrackInfo(ctx, params.name);
                }
                // re-download gpx
                const downloadFile = { ...currentFile, ...params };
                if (open) {
                    downloadAfterUpload(ctx, downloadFile, true).then();
                }
                deleteLocalTrack(ctx);
                refreshGlobalFiles({ ctx, currentFileName: params.name }).then();
                return true;
            }
            serverError = getServerErrorMessage(res);
        }
        const msg = i18n.t('web:save_error_msg', { name: fileName });
        ctx.setTrackErrorMsg({
            title: i18n.t('web:save_error_title'),
            msg: serverError ? `${msg}: ${serverError}` : msg,
        });
    }
    return false;
}

// nginx answers some errors with its own html page instead of the server text
export function getServerErrorMessage(res) {
    if (!res?.data || res.headers?.get('Content-Type')?.includes('text/html')) {
        return null;
    }
    try {
        const error = JSON.parse(res.data).error;
        return error.message ?? error;
    } catch {
        return res.data;
    }
}

export function removeFileExtension(filename) {
    return filename.includes('.') ? filename.slice(0, filename.lastIndexOf('.')) : filename;
}

// the longest file of a track, "<name>.gpx.info", has to fit the 255-byte limit of the phone file systems
function trackNameMaxBytes() {
    return FILE_NAME_MAX_BYTES - GPX_FILE_EXT.length - INFO_FILE_EXT.length;
}

export function sanitizedTrackName(name) {
    return sanitizedFileName(name, false, trackNameMaxBytes());
}

export function createTrackFreeName(name, otherTracks, folder = null, folderName = null, occupiedFileNames = null) {
    let occupied = null;
    let newName = truncateToBytes(name, trackNameMaxBytes());
    for (let i = 1; i < 100; i++) {
        if ((folder !== null || folderName !== null) && otherTracks) {
            //check cloud
            occupied = isTrackExists(newName, folder, folderName, otherTracks);
        } else {
            //check local
            occupied = otherTracks?.some((t) => t?.name === newName);
        }
        if (!occupied && !occupiedFileNames?.has(newName)) {
            return newName;
        }
        const suffix = ' - ' + i; // try with "Track - X"
        newName = truncateToBytes(name, trackNameMaxBytes() - suffix.length) + suffix;
    }
    if (occupied) {
        throw new Error('TracksManager addTrack() too many same-tracks');
    }
}

/**
 * Rename a browser-stored local track (IndexedDB + ctx.localTracks).
 * Same sanitization as cloud rename (RenameDialog + renameTrack flow).
 */
export function renameLocalTrack(ctx, oldName, rawNewName) {
    const newName = prepareName(sanitizedTrackName(rawNewName), true);
    if (!newName || newName.trim().length === 0) {
        return { error: i18n.t('web:rename_empty_track_name') };
    }
    const idx = ctx.localTracks.findIndex((t) => t?.name === oldName);
    if (idx === -1) {
        return { error: i18n.t('web:rename_track_not_found') };
    }
    if (ctx.localTracks.some((t, i) => i !== idx && t?.name === newName)) {
        return { error: i18n.t('web:rename_track_already_exists') };
    }
    if (newName === oldName) {
        return { ok: true };
    }
    const track = ctx.localTracks[idx];
    track.name = newName;
    track.id = newName;
    updateMetadata({ file: track, name: newName });

    if (ctx.selectedGpxFile?.name === oldName) {
        const prev = ctx.selectedGpxFile;
        updateMetadata({ file: prev, name: newName });
        ctx.setSelectedGpxFile({ ...prev, name: newName, id: newName, oldName });
    }
    if (ctx.selectedLocalTrackObj?.name === oldName) {
        ctx.setSelectedLocalTrackObj((prev) => ({ ...prev, name: newName }));
    }
    ctx.setLocalTracks([...ctx.localTracks]);
    saveTrackToLocalStorage({ ctx, track });
    return { ok: true };
}

export async function renameTrack(oldName, folder, newName, ctx) {
    const newFileName = folder + newName + GPX_FILE_EXT;
    if (newFileName !== oldName) {
        const res = await apiGet(`${process.env.REACT_APP_USER_API_SITE}/mapapi/rename-file`, {
            params: {
                oldName: oldName,
                newName: newFileName,
                type: 'GPX',
                saveCopy: false,
            },
            dataOnErrors: true,
        });
        if (res && res?.data?.status === 'ok') {
            await refreshGlobalFiles({ ctx, oldName, currentFileName: newFileName });
        } else {
            const msg = i18n.t('web:rename_error_msg', { name: prepareName(oldName) });
            const serverError = getServerErrorMessage(res);
            ctx.setTrackErrorMsg({
                title: i18n.t('web:rename_error_title'),
                msg: serverError ? `${msg}: ${serverError}` : msg,
            });
            return false;
        }
    }
    return true;
}

export async function updateGpxFiles(oldName, newFileName, listFiles, ctx) {
    if (!isEmpty(listFiles)) {
        //get gpx files
        let files = getGpxFiles(listFiles);
        if (ctx.gpxFiles[oldName]) {
            let newGpxFiles = cloneTrackObject(ctx.gpxFiles);
            for (const file of files) {
                if (file.name === newFileName) {
                    newGpxFiles[file.name] = preparedGpxFile({ file, oldFile: ctx.gpxFiles[oldName] });
                    if (newGpxFiles[file.name].url) {
                        const track = await TracksManager.loadTrackData(newGpxFiles[file.name]);
                        if (track) {
                            track.name = file.name;
                            const infoFile = findInfoFile(ctx, file.name);
                            track.info = infoFile?.details?.data;
                            Object.keys(track).forEach((t) => {
                                newGpxFiles[file.name][t] = track[t];
                            });
                            newGpxFiles[oldName].url = null;
                            ctx.setGpxFiles({ ...newGpxFiles });
                        } else {
                            newGpxFiles[oldName].url = null;
                            newGpxFiles[file.name].url = null;
                            ctx.setGpxFiles({ ...newGpxFiles });
                            ctx.setTrackErrorMsg({
                                title: i18n.t('web:open_error_title'),
                                msg: i18n.t('web:open_track_error_msg', { name: file.name }),
                            });
                        }
                    } else {
                        newGpxFiles[oldName].url = null;
                        ctx.setGpxFiles({ ...newGpxFiles });
                    }
                }
            }
        }
    } else {
        ctx.setGpxFiles({});
    }
}

export function updateVisibleTracks(oldN, newN) {
    let savedVisible = JSON.parse(localStorage.getItem(TRACK_VISIBLE_FLAG));
    if (savedVisible) {
        const newInd = savedVisible.new?.findIndex((n) => n === oldN);
        let oldInd;
        if (newInd === -1) {
            oldInd = savedVisible.old?.findIndex((n) => n === oldN);
            if (oldInd !== -1) {
                savedVisible.old[oldInd] = newN;
            }
        } else {
            savedVisible.new[newInd] = newN;
        }
        if (savedVisible.open) {
            const openInd = savedVisible.open?.findIndex((n) => n === oldN);
            if (openInd !== -1) {
                savedVisible.open[openInd] = newN;
            }
        }
        localStorage.setItem(TRACK_VISIBLE_FLAG, JSON.stringify(savedVisible));
    }
}

export async function duplicateTrack(oldName, folderName, newName, ctx) {
    newName = createTrackFreeName(newName, ctx.tracksGroups, null, folderName);
    let folder = folderName !== '' ? `${folderName}/` : '';
    const newFileName = folder + newName + GPX_FILE_EXT;
    if (newFileName !== oldName) {
        const res = await apiGet(`${process.env.REACT_APP_USER_API_SITE}/mapapi/rename-file`, {
            params: {
                oldName: oldName,
                newName: newFileName,
                type: 'GPX',
                saveCopy: true,
            },
            dataOnErrors: true,
        });
        if (res && res?.data?.status === 'ok') {
            refreshGlobalFiles({ ctx, currentFileName: newFileName }).then();
        } else {
            const msg = i18n.t('web:duplicate_error_msg', { name: prepareName(oldName) });
            const serverError = getServerErrorMessage(res);
            ctx.setTrackErrorMsg({
                title: i18n.t('web:duplicate_error_title'),
                msg: serverError ? `${msg}: ${serverError}` : msg,
            });
        }
    }
}

export async function renameFolder(folder, newName, ctx) {
    const newFolderName = renameLastFolderSegment(folder.fullName, newName);
    const res = await apiGet(`${process.env.REACT_APP_USER_API_SITE}/mapapi/rename-folder`, {
        params: {
            folderName: folder.fullName,
            newFolderName: newFolderName,
            type: 'GPX',
        },
        dataOnErrors: true,
    });
    if (res && res?.data?.status === 'ok') {
        updateSortList({ oldName: folder.fullName, newName: newFolderName, isTracks: true, ctx });
        refreshGlobalFiles({ ctx }).then();
    } else {
        ctx.setTrackErrorMsg({
            title: 'Duplicate error',
            msg: res.data,
        });
    }
}

export async function saveEmptyTrack(folderName, ctx) {
    //create empty file
    const convertedData = new TextEncoder().encode('');
    const zippedResult = require('pako').gzip(convertedData, { to: 'Uint8Array' });
    const convertedZipped = zippedResult.buffer;
    const oMyBlob = new Blob([convertedZipped], { type: 'gpx' });

    //create data
    const data = new FormData();
    data.append('file', oMyBlob, 'empty');
    const params = {
        type: 'GPX',
        name: `${folderName}/${EMPTY_FILE_NAME}`,
    };
    //save empty file with new folder's name
    const res = await apiPost(`${process.env.REACT_APP_USER_API_SITE}/mapapi/upload-file`, data, { params });
    if (res && res?.data?.status === 'ok') {
        refreshGlobalFiles({ ctx, currentFileName: params.name }).then();
        return true;
    }
    ctx.setTrackErrorMsg({
        title: i18n.t('web:create_folder_error_title'),
        msg: i18n.t('web:create_folder_error_msg', { name: folderName }),
    });
    return false;
}

export async function refreshGlobalFiles({
    ctx,
    oldName = null,
    currentFileName = null,
    type = GPX_FILE_TYPE,
    updateMarkers = true,
}) {
    // refresh list-files but skip if uploaded file is already there
    if (currentFileName == null || !ctx.listFiles.uniqueFiles?.find((f) => f.name === currentFileName)) {
        const respGetFiles = await apiGet(`${process.env.REACT_APP_USER_API_SITE}/mapapi/list-files`, {});
        const resJson = await respGetFiles.json();
        if (resJson && resJson.uniqueFiles) {
            getFilesForUpdateDetails(resJson.uniqueFiles, ctx.setUpdateFiles);
            ctx.setListFiles(resJson);
        }
        if (type === OBJECT_TYPE_FAVORITE) {
            return await updateFavGroups(resJson, ctx, updateMarkers);
        } else if (type === GPX_FILE_TYPE) {
            updateTrackGroups(resJson, ctx);
            if (oldName) {
                updateVisibleTracks(oldName, currentFileName);
                await updateGpxFiles(oldName, currentFileName, resJson, ctx);
            }
        }
    } else {
        if (type === GPX_FILE_TYPE) {
            const updatedTrackGroups = updateUpdatetimemsInGroups(ctx.tracksGroups, currentFileName, Date.now());
            ctx.setTracksGroups(updatedTrackGroups);
        }
    }
}

// after success upload from Local to Cloud
// download it and use as current Cloud track
async function downloadAfterUpload(ctx, file, showOnMap) {
    const createState = {
        enable: false, // stop-editor
    };

    // cleanup
    if (ctx.createTrack?.enable && ctx.selectedGpxFile) {
        createState.closePrev = {
            file: cloneTrackObject(ctx.selectedGpxFile),
        };
    }

    ctx.setCreateTrack({ ...createState });

    const newGpxFiles = { ...ctx.gpxFiles };

    newGpxFiles[file.name] = preparedGpxFile({ file });

    const track = await TracksManager.loadTrackData(newGpxFiles[file.name]);
    if (isEmptyTrack(track) === false) {
        const type = OBJECT_TYPE_CLOUD_TRACK;
        ctx.setUpdateInfoBlock(true);
        ctx.setCurrentObjectType(type);

        const infoFile = findInfoFile(ctx, file.name);
        track.info = infoFile?.details?.data;
        track.name = file.name;
        Object.keys(track).forEach((t) => {
            newGpxFiles[file.name][t] = track[t];
        });
        newGpxFiles[file.name].analysis = TracksManager.prepareAnalysis(newGpxFiles[file.name].analysis);
        newGpxFiles[file.name].showOnMap = showOnMap;
        if (showOnMap) {
            newGpxFiles[file.name].zoomToTrack = true;
        }
        ctx.setGpxFiles(newGpxFiles);
        ctx.setSelectedGpxFile({ ...newGpxFiles[file.name] });
    } else {
        ctx.setTrackErrorMsg({
            title: i18n.t('web:open_error_title'),
            msg: i18n.t('web:open_error_msg', { name: file.name }),
        });
    }
    ctx.setProcessingSaveTrack(false);
}

function updateTrackGroups(listFiles, ctx) {
    if (!isEmpty(listFiles)) {
        const files = getGpxFiles(listFiles);
        const trackGroups = createTrackGroups({ files, ctx });
        ctx.setTracksGroups(trackGroups);
    } else {
        const smartFolders = filterSmartFolders(ctx.tracksGroups);
        ctx.setTracksGroups(smartFolders);
    }
}

function updateUpdatetimemsInGroups(groups, fileName, newUpdatetimems) {
    const updateFile = (file) =>
        file.name === fileName ? { ...file, updatetimems: newUpdatetimems, updatetime: newUpdatetimems } : file;

    return groups.map((group) => {
        const updatedGroup = {
            ...group,
            subfolders: updateUpdatetimemsInGroups(group.subfolders, fileName, newUpdatetimems),
            groupFiles: group.groupFiles.map(updateFile),
            files: group.files.map(updateFile),
        };
        calculateLastModified(updatedGroup);

        return updatedGroup;
    });
}

function closeCloudTrack(ctx, track) {
    let gpx = ctx.gpxFiles[track.originalName];
    if (gpx) {
        gpx.url = null;
        ctx.setGpxFiles({ ...ctx.gpxFiles });
    }
}

// set copy of track with .overwrite <bool> and .selected = true
// overwrite flag used later when re-uploading (save to cloud)
// set type of current object, enable editor with "edit" flag
function openNewLocalTrack({ ctx, track, cloudAutoSave = false }) {
    const createState = {
        enable: true, // start-editor
        edit: true,
        cloudAutoSave,
    };

    // cleanup
    if (ctx.createTrack?.enable && ctx.selectedGpxFile) {
        createState.closePrev = {
            file: cloneTrackObject(ctx.selectedGpxFile),
        };
    }

    ctx.setCreateTrack(createState);

    // parent needs our track.selected = true
    track.selected = true; // track is ref
    ctx.setSelectedGpxFile({ ...track });

    ctx.setCurrentObjectType(OBJECT_TYPE_LOCAL_TRACK);
}
